import { useMemo, useState, useCallback, useEffect } from "react"
import { Radio, Input, Button, Flex, Dropdown, Badge } from "antd"
import { SearchOutlined, CalendarOutlined, ReloadOutlined, DownOutlined, CloseCircleOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons"
import { useSearchParams } from "react-router-dom"
import { getTimePeriods, FUTURE_ONLY_STATUSES, RESERVATION_STATUSES } from "../constants"
import { useTranslation } from "react-i18next"
import styles from "./header.module.css"
import DateSwitcher from "modules/datepicker/DateSwitcher"
import PropTypes from "prop-types"
import { DATE_FORMAT, MANIPULATION_TYPES } from "modules/datepicker/constants"
import dayjs from "dayjs"
import { DSIcon } from "ui/Icon/DSIcon"
import { useBreakpoints } from "modules/window/hooks/useBreakpoints"
import MobileDatePicker from "./MobileDatePicker"
import CustomDrawer from "ui/Drawer"
import DateRangeFilterPanel from "ui/Filters/DateRangeFilterPanel"
import CheckboxFilterPanel from "ui/Filters/CheckboxFilterPanel"
import { FILTER_PANEL_VARIANTS } from "ui/Filters/constants"

// Human-readable label for a custom date range, collapsing shared month/year.
const formatRangeLabel = (from, to) => {
  const start = dayjs(from)
  const end = dayjs(to)
  if (start.year() !== end.year()) return `${start.format("MMM D, YYYY")} – ${end.format("MMM D, YYYY")}`
  if (start.month() !== end.month()) return `${start.format("MMM D")} – ${end.format("MMM D, YYYY")}`
  return `${start.format("MMM D")} – ${end.format("D, YYYY")}`
}

// Left-side switcher shown while a custom Date Range is active; arrows shift the window by its span.
const CustomRangeSwitcher = ({ dateFrom, dateTo, onShift, className }) => (
  <Flex align="center" gap={4} className={className}>
    <Button type="text" size="small" icon={<LeftOutlined />} onClick={() => onShift(-1)} aria-label="Previous range" />
    <span className={styles.customRangeLabel}>{formatRangeLabel(dateFrom, dateTo)}</span>
    <Button type="text" size="small" icon={<RightOutlined />} onClick={() => onShift(1)} aria-label="Next range" />
  </Flex>
)

CustomRangeSwitcher.propTypes = {
  dateFrom: PropTypes.string.isRequired,
  dateTo: PropTypes.string.isRequired,
  onShift: PropTypes.func.isRequired,
  className: PropTypes.string
}

const BookingsHeader = ({
  onChangeFilters,
  variant = "upcoming",
  reservationStatus = "upcoming",
  onOpenFilters,
  experienceOptions = [],
  selectedExperiences = [],
  onApplyExperiences,
  selectedResources = [],
  onApplyResources,
  crewOptions = [],
  equipmentOptions = [],
  filterCount = 0
}) => {
  const isReservation = variant === "reservation"
  const breakpoint = useBreakpoints()
  const isMobile = ["xs", "xsm", "sm"].includes(breakpoint)
  const [isResourceFilterOpen, setIsResourceFilterOpen] = useState(false)
  const [isExperienceFilterOpen, setIsExperienceFilterOpen] = useState(false)
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false)
  const [isMobileDateOpen, setIsMobileDateOpen] = useState(false)
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false)
  const { t } = useTranslation()
  const timePeriod = getTimePeriods(t)
  const [searchParams, setSearchParams] = useSearchParams()
  const range = searchParams.get("range")
  const search = searchParams.get("search")
  const currentDate = searchParams.get("date")
  const dateFrom = searchParams.get("date_from")
  const dateTo = searchParams.get("date_to")
  let date = useMemo(() => (currentDate ? window.dateTime(currentDate) : null), [currentDate])

  // Local mirror of the search param so the field updates instantly while the
  // fetch is debounced (the URL param only changes after the debounce fires).
  const [searchInput, setSearchInput] = useState(search || "")
  useEffect(() => {
    setSearchInput(search || "")
  }, [search])

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value)
    onChangeFilters(e)
  }

  // Future-only date scope: the Upcoming Experiences board is always future-only, and within
  // All Reservations the Upcoming/Requests sub-tabs are future-only too (backend welds
  // booked_date >= today). Completed/Cancelled span all dates, so they stay unrestricted.
  const isFutureOnly = useMemo(
    () => (isReservation ? FUTURE_ONLY_STATUSES.includes(reservationStatus) : true),
    [isReservation, reservationStatus]
  )

  // Past dates are blocked from today onward whenever the current view is future-only.
  // Snap minDate to the START of the active range unit, matching how the date anchor is
  // snapped (Month view anchors on the 1st). Otherwise the current month's anchor (e.g. Aug 1)
  // falls before start-of-today (Aug 7) and the picker wrongly blocks stepping back into the
  // current month, even though it isn't fully past.
  const restrictions = useMemo(() => {
    if (!isFutureOnly) return {}
    // Map the view range to a tempus-dominus Unit ("date"/"weekDay"/"month") — its startOf
    // rejects moment-style units like "day"/"week".
    const unit = range === "week" ? MANIPULATION_TYPES.week : range === "day" ? MANIPULATION_TYPES.date : MANIPULATION_TYPES.month
    return { minDate: window.dateTime().startOf(unit) }
  }, [isFutureOnly, range])

  // dayjs mirror of the min date for the mobile date controls (null = unrestricted).
  const minDate = useMemo(() => (isFutureOnly ? dayjs().startOf("day") : null), [isFutureOnly])

  // On the upcoming tab, block stepping the mobile date bar back into a fully-past period.
  const canGoPrev = useMemo(() => {
    if (!minDate || !currentDate) return true
    const unit = range === "day" ? "day" : range || "month"
    return !dayjs(currentDate).subtract(1, unit).endOf(unit).isBefore(minDate, "day")
  }, [minDate, range, currentDate])

  const handleToday = () => {
    onChangeFilters({ target: { name: "date", value: dayjs().format(DATE_FORMAT) } })
  }

  // The shared filter panel takes labelled groups of options; crew and equipment ids are
  // prefixed because they are only unique within their own list.
  const resourceFilterGroups = useMemo(
    () => [
      {
        key: "crew",
        label: t("bookings.crew", "Crew"),
        iconName: "users",
        options: crewOptions.map((crew) => ({ id: `crew-${crew.id}`, label: crew.name, suffix: crew.role ? `(${crew.role})` : undefined }))
      },
      {
        key: "equipment",
        label: t("bookings.equipment", "Equipment"),
        iconName: "box",
        options: equipmentOptions.map((item) => ({ id: `equip-${item.id}`, label: item.name }))
      }
    ],
    [crewOptions, equipmentOptions, t]
  )

  const experienceFilterGroups = useMemo(
    () => [{ key: "experiences", options: experienceOptions.map((option) => ({ id: option.value, label: option.label })) }],
    [experienceOptions]
  )

  const dateRangeValue = dateFrom && dateTo ? [dayjs(dateFrom), dayjs(dateTo)] : null
  const hasDateRange = !!dateRangeValue

  const handleDateRangeChange = useCallback(
    (dates) => {
      // Set both bounds in one update — two back-to-back setSearchParams calls read the
      // same stale params, so the second would drop date_from and break the badge/chip.
      setSearchParams((prev) => {
        const updated = new URLSearchParams(prev.toString())
        if (dates?.length === 2) {
          updated.set("date_from", dates[0].format(DATE_FORMAT))
          updated.set("date_to", dates[1].format(DATE_FORMAT))
          // Enter custom mode: this deselects Day/Week/Month and drives the list off the range.
          updated.set("range", "custom")
        } else {
          // Clearing the range reverts to the default Month period.
          updated.delete("date_from")
          updated.delete("date_to")
          updated.set("range", "month")
          updated.set("date", dayjs().startOf("month").format(DATE_FORMAT))
        }
        return updated
      })
      setIsDateRangeOpen(false)
    },
    [setSearchParams]
  )

  const shiftCustomRange = useCallback(
    (direction) => {
      const start = dayjs(dateFrom)
      const end = dayjs(dateTo)
      const span = end.diff(start, "day") + 1
      setSearchParams((prev) => {
        const updated = new URLSearchParams(prev.toString())
        updated.set("date_from", start.add(direction * span, "day").format(DATE_FORMAT))
        updated.set("date_to", end.add(direction * span, "day").format(DATE_FORMAT))
        return updated
      })
    },
    [dateFrom, dateTo, setSearchParams]
  )

  const renderResourcePanel = useCallback(
    () => (
      <CheckboxFilterPanel
        title={t("filters.title", "Filter")}
        groups={resourceFilterGroups}
        selectedIds={selectedResources}
        onApply={(selected) => {
          onApplyResources?.(selected)
          setIsResourceFilterOpen(false)
        }}
        onClose={() => setIsResourceFilterOpen(false)}
      />
    ),
    [resourceFilterGroups, selectedResources, onApplyResources, t]
  )

  const renderExperiencePanel = useCallback(
    () => (
      <CheckboxFilterPanel
        groups={experienceFilterGroups}
        selectedIds={selectedExperiences}
        onApply={(selected) => {
          onApplyExperiences?.(selected)
          setIsExperienceFilterOpen(false)
        }}
        onClose={() => setIsExperienceFilterOpen(false)}
      />
    ),
    [experienceFilterGroups, selectedExperiences, onApplyExperiences]
  )

  // Rendered in both variants: inside the Date Range group for By Experience, and between the
  // search field and the Experiences filter for By Reservation.
  const resourceFilterDropdown = (
    <Dropdown
      open={isResourceFilterOpen}
      onOpenChange={setIsResourceFilterOpen}
      trigger={["click"]}
      dropdownRender={renderResourcePanel}
    >
      <Button
        size="large"
        className={`${styles.filterBtn} ${selectedResources.length > 0 || isResourceFilterOpen ? styles.filterBtnActive : ""}`}
      >
        <DSIcon name="box" width={16} height={16} />
        {t("bookings.resources", "Resources")}
        {selectedResources.length > 0 && <span className={styles.filterBtnBadge}>{selectedResources.length}</span>}
        <DownOutlined />
      </Button>
    </Dropdown>
  )

  const isCustomRange = range === "custom" && hasDateRange

  // Mobile date bar (‹ label ›): arrows step by the active period, the label opens the
  // Select Day/Week/Month bottom sheet. Isolated from the legacy DateSwitcher/flatpickr.
  const navigateMobileDate = (direction) => {
    if (direction < 0 && !canGoPrev) return
    const unit = range === "day" ? "day" : range || "month"
    const next = dayjs(currentDate).add(direction, unit).format(DATE_FORMAT)
    onChangeFilters({ target: { name: "date", value: next } })
  }

  const mobileDateLabel = useMemo(() => {
    const parsed = dayjs(currentDate)
    if (!currentDate || !parsed.isValid()) return ""
    if (range === "day") return parsed.format("MMM D, YYYY")
    if (range === "week") return `${parsed.startOf("week").format("MMM D")} - ${parsed.endOf("week").format("MMM D, YYYY")}`
    return parsed.format("MMMM YYYY")
  }, [currentDate, range])

  const renderDatePicker = () => {
    if (isCustomRange) {
      return <CustomRangeSwitcher className={styles.customRangeSwitcher} dateFrom={dateFrom} dateTo={dateTo} onShift={shiftCustomRange} />
    }
    if (!date) return null
    if (isMobile) {
      return (
        <Flex align="center" justify="space-between" className={styles.mobileDateBar}>
          <Button type="text" icon={<LeftOutlined />} disabled={!canGoPrev} onClick={() => navigateMobileDate(-1)} aria-label="Previous period" />
          <button type="button" className={styles.mobileDateLabel} onClick={() => setIsMobileDateOpen(true)}>
            {mobileDateLabel}
          </button>
          <Button type="text" icon={<RightOutlined />} onClick={() => navigateMobileDate(1)} aria-label="Next period" />
        </Flex>
      )
    }
    return (
      <DateSwitcher
        className={styles.dateSwitcher}
        value={date}
        // Remount when the value OR the restriction regime changes. The shared Datepicker
        // blanks its display (renders "—") if `restrictions` change at runtime while the value
        // stays the same, so switching future-only ↔ all-dates tabs needs a fresh mount.
        key={`${currentDate}-${isFutureOnly ? "future" : "all"}`}
        defaultValue={window.dateTime().startOf("date")}
        name="date"
        type={range === "custom" ? "month" : range || "month"}
        onChange={onChangeFilters}
        restrictions={restrictions}
      />
    )
  }

  const datePicker = renderDatePicker()

  const activeChips = useMemo(() => {
    const chips = []
    if (hasDateRange)
      chips.push({ key: "date-range", label: `Custom Range: ${dayjs(dateFrom).format("MMM D, YYYY")} – ${dayjs(dateTo).format("MMM D, YYYY")}` })
    return chips
  }, [hasDateRange, dateFrom, dateTo])

  const removeChip = useCallback(
    (key) => {
      const updated = new URLSearchParams(searchParams.toString())
      if (key === "date-range") {
        updated.delete("date_from")
        updated.delete("date_to")
        // Removing the range exits custom mode and returns to the default Month period.
        updated.set("range", "month")
        updated.set("date", dayjs().startOf("month").format(DATE_FORMAT))
        setSearchParams(updated)
      }
    },
    [searchParams, setSearchParams]
  )

  return (
    <Flex vertical className={styles.headerContainer}>
      <MobileDatePicker
        open={isMobileDateOpen}
        onClose={() => setIsMobileDateOpen(false)}
        range={range === "custom" ? "month" : range || "month"}
        value={currentDate ? dayjs(currentDate) : null}
        name="date"
        minDate={minDate}
        onChange={onChangeFilters}
      />

      {/* Upcoming Experiences: the sliders button opens the Resources filter as a bottom sheet
          (the desktop Date Range / Resources buttons are hidden on mobile). */}
      {!isReservation && (
        <CustomDrawer open={isMobileFilterOpen} setOpen={setIsMobileFilterOpen} title="Filters">
          <CheckboxFilterPanel
            variant={FILTER_PANEL_VARIANTS.INLINE}
            groups={resourceFilterGroups}
            selectedIds={selectedResources}
            onApply={(selected) => {
              onApplyResources?.(selected)
              setIsMobileFilterOpen(false)
            }}
            onClose={() => setIsMobileFilterOpen(false)}
          />
        </CustomDrawer>
      )}
      <Flex gap={8} align="center" wrap="wrap" className={styles.headerRow} justify="space-between">
        <Flex align="center" gap={8} className={styles.navGroup}>
          <Button icon={<ReloadOutlined />} className={styles.todayBtn} onClick={handleToday}>
            {t("bookings.today")}
          </Button>

          {datePicker}

          <Radio.Group name="range" value={range} className={styles.radioButtonSwitch} onChange={onChangeFilters}>
            {Object.entries(timePeriod).map(([key, label]) => (
              <Radio.Button name="range" key={key} value={key} className={styles.radioButton} onChange={onChangeFilters}>
                {label}
              </Radio.Button>
            ))}
          </Radio.Group>
        </Flex>

        <Flex align="center" gap={8} className={styles.searchGroup}>
          {!isReservation && (
            <Flex align="center" gap={8} className={styles.filtersGroup}>
              <div className={styles.dateRangeWrapper}>
                <Button
                  size="large"
                  className={`${styles.filterBtn} ${hasDateRange ? styles.filterBtnActive : ""}`}
                  onClick={() => setIsDateRangeOpen(true)}
                >
                  <CalendarOutlined />
                  {t("bookings.date_range", "Date Range")}
                  {hasDateRange && <span className={styles.filterBtnBadge}>1</span>}
                  <DownOutlined />
                </Button>
                <DateRangeFilterPanel
                  isOpen={isDateRangeOpen}
                  onOpenChange={setIsDateRangeOpen}
                  value={dateRangeValue}
                  onChange={handleDateRangeChange}
                  disabledDate={(current) => !!minDate && !!current && current.isBefore(minDate, "day")}
                  format={DATE_FORMAT}
                />
              </div>
              {resourceFilterDropdown}
            </Flex>
          )}
          <Input
            prefix={<SearchOutlined aria-hidden="true" />}
            placeholder={t("bookings.search_booking", "Search a booking by Id or experience/client name")}
            className={styles.searchInput}
            name="search"
            allowClear
            value={searchInput}
            onChange={handleSearchChange}
          />
          {isReservation && resourceFilterDropdown}
          {isReservation && (
            <Dropdown
              open={isExperienceFilterOpen}
              onOpenChange={setIsExperienceFilterOpen}
              trigger={["click"]}
              dropdownRender={renderExperiencePanel}
            >
              <Button
                size="large"
                type="button"
                className={`${styles.filterBtn} ${selectedExperiences.length > 0 || isExperienceFilterOpen ? styles.filterBtnActive : ""}`}
              >
                {t("bookings.experiences", "Experiences")}
                {selectedExperiences.length > 0 && <span className={styles.filterBtnBadge}>{selectedExperiences.length}</span>}
                <DownOutlined />
              </Button>
            </Dropdown>
          )}
          <Badge
            count={isReservation ? filterCount : selectedResources.length}
            size="small"
            offset={[-4, 4]}
            className={`${styles.filterSlidersBadge} ${isReservation ? styles.filterSlidersBadgeShow : ""}`}
          >
            <Button
              shape="circle"
              color="primary"
              variant="outlined"
              className={`${styles.filterSlidersBtn} ${isReservation ? styles.filterSlidersBtnShow : ""}`}
              icon={<DSIcon name="filterSliders" width={20} height={20} fill="var(--color-primary)" />}
              onClick={() => (isReservation ? onOpenFilters?.current?.() : setIsMobileFilterOpen(true))}
            />
          </Badge>
        </Flex>
      </Flex>

      {(activeChips.length > 0 || selectedResources.length > 0) && (
        <Flex align="center" gap={8} wrap="wrap" className={styles.chipsRow}>
          {activeChips.map((chip) => (
            <span key={chip.key} className={styles.chipRectangle}>
              <span className={styles.chipLabel}>{chip.label}</span>
              <button
                type="button"
                className={styles.chipRemove}
                onClick={() => removeChip(chip.key)}
                aria-label={`Remove ${chip.label}`}
              >
                <CloseCircleOutlined />
              </button>
            </span>
          ))}
          {selectedResources.map((key) => {
            const allItems = [
              ...crewOptions.map((c) => ({ id: `crew-${c.id}`, name: c.name })),
              ...equipmentOptions.map((e) => ({ id: `equip-${e.id}`, name: e.name }))
            ]
            const item = allItems.find((i) => i.id === key)
            return item ? (
              <span key={key} className={styles.chipRectangle}>
                <span className={styles.chipLabel}>{item.name}</span>
                <button
                  type="button"
                  className={styles.chipRemove}
                  onClick={() => onApplyResources?.(selectedResources.filter((r) => r !== key))}
                  aria-label={`Remove ${item.name}`}
                >
                  <CloseCircleOutlined />
                </button>
              </span>
            ) : null
          })}
        </Flex>
      )}
    </Flex>
  )
}

BookingsHeader.propTypes = {
  onChangeFilters: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(["upcoming", "reservation"]),
  reservationStatus: PropTypes.oneOf(RESERVATION_STATUSES),
  onOpenFilters: PropTypes.shape({ current: PropTypes.func }),
  experienceOptions: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.number, label: PropTypes.string })),
  selectedExperiences: PropTypes.arrayOf(PropTypes.number),
  onApplyExperiences: PropTypes.func,
  selectedResources: PropTypes.arrayOf(PropTypes.string),
  onApplyResources: PropTypes.func,
  crewOptions: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string, role: PropTypes.string })),
  equipmentOptions: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
  filterCount: PropTypes.number
}

export default BookingsHeader
